import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecuritySection = () => {
  const certifications = [
    {
      name: 'SOC 2 Type II',
      icon: 'ShieldCheck',
      description: 'Audited security controls',
      badge: 'Certified 2024',
      color: 'text-success'
    },
    {
      name: 'ISO 27001',
      icon: 'Award',
      description: 'Information security management',
      badge: 'Certified 2023',
      color: 'text-primary'
    },
    {
      name: 'GDPR Compliant',
      icon: 'Shield',
      description: 'EU data protection standards',
      badge: 'Verified',
      color: 'text-accent'
    },
    {
      name: 'ISO 27701',
      icon: 'Lock',
      description: 'Privacy information management',
      badge: 'Certified 2024',
      color: 'text-warning'
    },
  ];

  const securityMeasures = [
    {
      category: 'Data Protection',
      icon: 'Database',
      measures: [
        'AES-256 encryption at rest and TLS 1.3 in transit',
        'Multi-region data replication with 99.999% durability',
        'Automated daily backups with 90-day retention',
        'Field-level encryption for sensitive data (SSN, payment info)'
      ]
    },
    {
      category: 'Access Control',
      icon: 'Key',
      measures: [
        'Single Sign-On (SSO) via Okta, Azure AD, Google Identity',
        'Multi-factor authentication (MFA) enforced for all users',
        'Role-based access control (RBAC) with granular permissions',
        'Automated session timeout and concurrent login limits'
      ]
    },
    {
      category: 'Infrastructure Security',
      icon: 'Server',
      measures: [
        'AWS-hosted with VPC isolation and private subnets',
        'Web Application Firewall (WAF) with DDoS protection',
        'Intrusion detection and prevention systems (IDS/IPS)',
        'Zero-trust network architecture with micro-segmentation'
      ]
    },
    {
      category: 'Compliance & Monitoring',
      icon: 'Activity',
      measures: [
        'Real-time security event monitoring (SIEM)',
        '24/7 Security Operations Center (SOC) with incident response',
        'Quarterly penetration testing by third-party auditors',
        'Annual compliance audits for SOC2, ISO 27001, HIPAA'
      ]
    },
  ];

  return (
    <section id="security" className="scroll-mt-20">
      <div className="bg-card/30 backdrop-blur-sm border border-border rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center">
            <Icon name="Lock" size={32} className="text-warning" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-foreground">Security & Compliance</h2>
            <p className="text-muted-foreground mt-1">Enterprise-grade security built for global scale</p>
          </div>
        </div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {certifications?.map((cert, index) => (
            <div 
              key={index}
              className="bg-background/50 border border-border rounded-lg p-6 hover:border-warning/50 hover:shadow-glow transition-all text-center"
            >
              <div className={`w-14 h-14 ${cert?.color?.replace('text', 'bg')}/20 rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Icon name={cert?.icon} size={28} className={cert?.color} />
              </div>
              <h3 className="font-bold text-foreground mb-1">{cert?.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{cert?.description}</p>
              <span className="inline-block px-3 py-1 bg-success/20 text-success text-xs font-semibold rounded-full">
                {cert?.badge}
              </span>
            </div>
          ))}
        </div>

        {/* Security Measures */}
        <div className="space-y-6 mb-8">
          {securityMeasures?.map((section, index) => (
            <div 
              key={index}
              className="bg-background/50 border border-border rounded-lg p-6 hover:border-warning/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name={section?.icon} size={20} className="text-warning" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{section?.category}</h3>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section?.measures?.map((measure, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{measure}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Incident Response */}
        <div className="p-6 bg-warning/5 border border-warning/20 rounded-lg mb-8">
          <div className="flex items-start gap-4">
            <Icon name="AlertOctagon" size={24} className="text-warning flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-foreground mb-2">Security Incident Response</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Our Security Incident Response Team (SIRT) operates 24/7 with &lt;15 minute response time for P0 incidents. All enterprise customers receive real-time security notifications and quarterly security reviews.
              </p>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="px-3 py-1 bg-success/20 text-success rounded-full">Mean Time to Detect: 2.4 min</span>
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full">Mean Time to Respond: 12 min</span>
                <span className="px-3 py-1 bg-accent/20 text-accent rounded-full">Zero Data Breaches Since 2019</span>
              </div>
            </div>
          </div>
        </div>

        {/* Downloadable Resources */}
        <div className="flex flex-wrap gap-4">
          <Button 
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={() => window.open('/security-whitepaper.pdf', '_blank')}
          >
            Security Whitepaper
          </Button>
          <Button 
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            onClick={() => window.open('/compliance-reports.pdf', '_blank')}
          >
            Compliance Reports
          </Button>
          <Button 
            variant="outline"
            iconName="Shield"
            iconPosition="left"
            onClick={() => window.open('/penetration-test-summary.pdf', '_blank')}
          >
            Pen Test Summary
          </Button>
          <Button 
            variant="ghost"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.open('mailto:security@firstondate.com', '_blank')}
          >
            Report Vulnerability
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;